import { createContext, useState, useContext, ReactNode, useEffect } from 'react';
import { ApiService } from './services/api';

interface User {
    id: number;
    username: string;
    email: string;
    is_admin: boolean;
    email_verified: boolean;
    date_joined?: string;
}

interface AuthContextType {
    user: User | null;
    login: (userData: User) => void;
    logout: () => void;
    isAuthenticated: boolean;
    loading: boolean;
    needsEmailVerification: boolean;
    verificationDaysLeft: number;
    canAccessContent: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
    const [user, setUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);
    const [needsEmailVerification, setNeedsEmailVerification] = useState(false);
    const [verificationDaysLeft, setVerificationDaysLeft] = useState(0);

    // Email verification period configuration
    const VERIFICATION_DAYS = 30;

    useEffect(() => {
        let cancelled = false;
        
        const checkEmailVerification = (userData: User) => {
            if (!userData.email_verified && userData.date_joined) {
                const joinedDate = new Date(userData.date_joined);
                const now = new Date();
                const timeDiff = now.getTime() - joinedDate.getTime();
                const daysSinceJoined = Math.floor(timeDiff / (1000 * 3600 * 24));
                const daysLeft = Math.max(0, VERIFICATION_DAYS - daysSinceJoined);


                if (daysLeft > 0) {
                    setNeedsEmailVerification(true);
                    setVerificationDaysLeft(daysLeft);
                } else {
                    // Past 30 days - verification required
                    setNeedsEmailVerification(true);
                    setVerificationDaysLeft(0);
                }
            } else {
                // Email verified or no date_joined (legacy users)
                setNeedsEmailVerification(false);
                setVerificationDaysLeft(0);
            }
        };

        const checkUserSession = async () => {
            // Only check API if we don't already have a user (avoid overriding login state)
            if (!user) {
                try {
                    const userData = await ApiService.getCurrentUser();
                    if (!cancelled) {
                        setUser(userData);
                        // Check email verification for authenticated users
                        checkEmailVerification(userData);
                    }
                } catch (error: any) {
                    // Silently fail - user is just not authenticated
                    if (!cancelled) {
                        setUser(null);
                        setNeedsEmailVerification(false);
                        setVerificationDaysLeft(0);
                    }
                }
            } else {
                // User already authenticated, check email verification
                if (!cancelled) {
                    checkEmailVerification(user);
                }
            }

            if (!cancelled) {
                setLoading(false);
            }
        };

        // Don't block initial render - check auth in background
        checkUserSession();
        
        return () => {
            cancelled = true;
        };
    }, []);

    const login = (userData: User) => {
        setUser(userData);
    };

    const logout = async () => {
        try {
            await ApiService.logout();
        } catch (error) {
            console.error('Failed to logout on backend', error);
        } finally {
            setUser(null);
        }
    };

    const isAuthenticated = !!user;
    // Unauthenticated users always have access, authenticated users need email verification within 30 days
    const canAccessContent = !isAuthenticated || (isAuthenticated && (!needsEmailVerification || verificationDaysLeft > 0));

    return (
        <AuthContext.Provider value={{
            user,
            login,
            logout,
            isAuthenticated,
            loading,
            needsEmailVerification,
            verificationDaysLeft,
            canAccessContent
        }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
